
import { useTheme } from '@mui/material';
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { FC, useContext, useEffect, useMemo, useRef } from 'react';

import { useParams } from 'react-router-dom';
import { Species, WeightedHeatmapLocation } from '../../api/dtos/dtos';
import { AppSettings } from '../../AppSettings';
import MapContext, { MapFilterSubmitter } from '../../contexts/data/MapContext';
import AuthContext from '../../contexts/auth/AuthContext';
import { SpeciesContext } from '../../contexts/data/SpeciesContext';
import UserGroupsContext from '../../contexts/data/UserGroupsContext';

interface IKmlLayerProps {
    show: boolean;
    data?: WeightedHeatmapLocation[];
}

const KmlLayer: FC<IKmlLayerProps> = () => {
    const map = useMap();
    const kmlApi = useMapsLibrary('maps');
    const theme = useTheme();

    const { filter, mapCenter } = useContext(MapContext);
    const { user } = useContext(AuthContext);
    const { species: { species } } = useContext(SpeciesContext);
    const { data: userGroups } = useContext(UserGroupsContext);

    const { species: speciesName } = useParams();

    const kmlLayersRef = useRef<google.maps.KmlLayer[]>([]);
    const polygonsRef = useRef<google.maps.Polygon[]>([]);

    const isAdminOrSuperAdmin = useMemo(() => {
        return !!user?.roles?.find(x => x === 'admin' || x === 'superadmin');
    }, [user]); 

    const isRabbitDiseaseK5StateCoordinators = useMemo(() => {
        return !!user?.roles?.find(x => x === 'rabbit_disease_k5_state_coordinators');
    }, [user]);

    const isDataAccessForThisSpecies = useMemo(() => {
        return (
            user?.matchingRegionAdminRoles?.some(
                x => x.speciesId == null || x.speciesId === species
            ) ?? false
        );
    }, [user?.matchingRegionAdminRoles, species]);

    const createKmlLayer = (
        url: string,
        clickable = false,
        preserveViewport = false,
        type: string,
        meta?: {
            groupId?: number;
            fileName?: string;
        }
    ) => {
        if (!map || !kmlApi || !url) {
            return null;
        }

        // Prevent duplicates
        const alreadyExists = kmlLayersRef.current.some(l => (l as any).__url === url);

        if (alreadyExists) {
            return null;
        }

        const layer = new kmlApi.KmlLayer({
            url,
            clickable,
            preserveViewport
        });

        layer.setMap(map);

        // Metadata
        (layer as any).__url = url;
        (layer as any).__type = type;

        if (meta?.groupId != null) {
            (layer as any).__groupId = meta.groupId;
        }

        if (meta?.fileName) {
            (layer as any).__fileName = meta.fileName;
        }

        kmlLayersRef.current.push(layer);

        return layer;
    };

    const createPolygon = (polygonJson?: string) => {
        if (!map || !polygonJson) {
            return null;
        }

        try {
            const paths: google.maps.LatLngLiteral[] = JSON.parse(polygonJson);

            const polygon = new google.maps.Polygon({
                paths,
                strokeColor: theme.palette.primary.main,
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: theme.palette.primary.main,
                fillOpacity: 0.15
            });

            polygon.setMap(map);
            polygonsRef.current.push(polygon);

            const bounds = new google.maps.LatLngBounds();
            paths.forEach(coord => bounds.extend(coord));

            return { polygon, paths };
        } catch (err) {
            console.error("Failed to parse polygon JSON:", err);
            return null;
        }
    };

    // -- Data access regions --
    useEffect(() => {
        if (!map || !kmlApi) {
            return;
        }

        const isValidAccess =
            !isAdminOrSuperAdmin &&
            isDataAccessForThisSpecies &&
            filter.bySubmitter.submitter === MapFilterSubmitter.AllData &&
            filter.bySubmitter.submitterLabel !== "All Data";

        const removeAllRegionPolygons = () => {
            polygonsRef.current.forEach(poly => poly.setMap(null));
            polygonsRef.current = [];
        };

        if (!isValidAccess) {
            removeAllRegionPolygons();
            return;
        }

        removeAllRegionPolygons();

        const roles = user?.matchingRegionAdminRoles?.filter(
                x => x.speciesId == null || x.speciesId === species
            ) ?? [];

        const combinedBounds = new google.maps.LatLngBounds();
        let hasBounds = false;

        roles.forEach(role => {
            const region = role?.region;

            if (region?.polygon) {
                const result = createPolygon(region.polygon);

                if (result) {
                    result.paths.forEach(coord => {
                        combinedBounds.extend(coord);
                        hasBounds = true;
                    });
                }
            }
        });

        if (hasBounds) {
            map.fitBounds(combinedBounds);
        }

    }, [
        map,
        kmlApi,
        filter.bySubmitter,
        user,
        isAdminOrSuperAdmin,
        isDataAccessForThisSpecies,
        species
    ]);

    // -- Custom home page regions -- 
    useEffect(() => {
        if (!map || !kmlApi) {
            return;
        }

        const selectedTitles = new Set(
            filter.layers?.selectedCustomHomePageRegions ?? []
        );

        const regionList = filter.layers?.customHomePageRegions ?? [];

        const selectedFileNames = new Set(
            regionList
                .filter(x => selectedTitles.has(x.title))
                .map(x => x.fileName)
                .filter(Boolean)
        );

        // Remove unmatching regions
        const remaining: google.maps.KmlLayer[] = [];

        kmlLayersRef.current.forEach(layer => {
            const url = layer.getUrl?.();

            const isRegion = url?.includes("regions%2F");

            if (!isRegion) {
                remaining.push(layer);
                return;
            }

            const isStillSelected = Array.from(selectedFileNames).some(fileName =>
                url?.includes(fileName)
            );

            if (!isStillSelected) {
                layer.setMap(null);
                return;
            }

            remaining.push(layer);
        });

        kmlLayersRef.current = remaining;

        // Add missing regions
        if (selectedTitles.size && regionList.length) {
            selectedTitles.forEach(title => {
                const kmlFileName = regionList.find(x => x.title === title)?.fileName;

                if (!kmlFileName) {
                    return;
                }

                const url = `${AppSettings.kmlUrl}${AppSettings.basePath}/GetKML?pathInfo=regions%2F${kmlFileName}`;

                createKmlLayer(url, false, false, "region");
            });
        }
    }, [
        map,
        kmlApi,
        filter.layers?.selectedCustomHomePageRegions,
        filter.layers?.customHomePageRegions
    ]);

    // -- Community group kmls -- 
    useEffect(() => {
        if (!map || !kmlApi) {
            return;
        }

        const currentGroupId = filter.bySubmitter.communityGroupId ?? null;

        const isMyGroup =
            !!currentGroupId &&
            filter.bySubmitter.submitter === MapFilterSubmitter.MyGroup;

        const communityGroup = userGroups?.communityGroups?.find(
            x => x.communityGroupId == currentGroupId
        );

        const groupFileName = communityGroup?.kmlFileName?.split("?")[0];

        const prefix = communityGroup?.groupRegionKml ? "uploads" : "groups";

        // Cleanup
        const cleanupUnmatched = () => {
            const remaining: google.maps.KmlLayer[] = [];

            const selectedIds = new Set(filter.layers.selectedCommunityGroupKmls);

            kmlLayersRef.current.forEach(layer => {
                const type = (layer as any).__type;
                const url = (layer as any).__url;

                if (!url) {
                    layer.setMap(null);
                    return;
                }

                // Group layer
                if (type === "group") {
                    const stillValid = isMyGroup && (layer as any).__groupId === currentGroupId;

                    if (!stillValid) {
                        layer.setMap(null);
                        return;
                    }

                    remaining.push(layer);
                    return;
                }

                // Additional layer
                if (type === "additional") {
                    const fileName = (layer as any).__fileName;

                    const stillSelected =
                        fileName &&
                        filter.layers.communityGroupKmls.some(kml => {
                            const f = kml?.kmlFileName?.split("?")[0];
                            return (
                                f === fileName &&
                                selectedIds.has(kml.id)
                            );
                        });

                    if (!stillSelected) {
                        layer.setMap(null);
                        return;
                    }

                    remaining.push(layer);
                    return;
                }

                // fallback keep
                remaining.push(layer);
            });

            kmlLayersRef.current = remaining;
        };

        // Group kml
        if (isMyGroup && groupFileName) {
            const url = `${AppSettings.kmlUrl}${AppSettings.basePath}/GetKML?pathInfo=${prefix}%2F${groupFileName}`;

            createKmlLayer(
                url, 
                false, 
                mapCenter ? true : false, 
                "group", 
                { groupId: currentGroupId }
            );
        }

        // Additional kmls
        filter.layers.selectedCommunityGroupKmls.forEach(value => {
            if (value === -1) {
                return;
            }

            const kml = filter.layers.communityGroupKmls.find(f => f.id === value);

            const fileName = kml?.kmlFileName?.split("?")[0];

            if (!fileName) {
                return;
            }

            const url = `${AppSettings.kmlUrl}${AppSettings.basePath}/GetKML?pathInfo=additionalKML%2F${fileName}`;

            createKmlLayer(
                url,
                kml?.clickable,
                mapCenter ? true : false,
                "additional",
                { fileName }
            );
        });

        // Clean up
        cleanupUnmatched();
    }, [
        map,
        kmlApi,
        filter.bySubmitter.communityGroupId,
        filter.layers.communityGroupKmls,
        filter.layers.selectedCommunityGroupKmls,
        userGroups
    ]);

    // -- Barrier fences --
    useEffect(() => {
        if (!map || !kmlApi) {
            return;
        }

        if (![Species.WildDog, Species.Rabbit, Species.FeralFish].includes(species)) {
            return;
        }

        const speciesKmlName = speciesName?.replace(/scan|alert/g, "");
        if (!speciesKmlName) {
            return;
        }

        const url = `${AppSettings.kmlUrl}${AppSettings.basePath}/GetKML?pathInfo=barrierFences%2F${speciesKmlName}_barrier_fence.kml`;

        const isEnabled = filter.layers.barrierFences;

        if (isEnabled) {
            createKmlLayer(url, false, true, "barrierfence");
        } else {
            const remaining: google.maps.KmlLayer[] = [];

            kmlLayersRef.current.forEach(layer => {
                const matches = layer.getUrl?.()?.includes(`barrierFences%2F${speciesKmlName}_barrier_fence.kml`);

                if (matches) {
                    layer.setMap(null);
                } else {
                    remaining.push(layer);
                }
            });

            kmlLayersRef.current = remaining;
        }
    }, [map, kmlApi, filter.layers.barrierFences, species, speciesName, kmlLayersRef.current]);

    // -- Known regions --
    useEffect(() => {
        if (!map || !kmlApi) {
            return;
        }

        const speciesForKnownRegions = [
            Species.WildDog,
            Species.Deer,
            Species.Rabbit,
            Species.Fox,
            Species.FeralPig,
            Species.Myna,
            Species.Toad,
            Species.FeralGoat,
            Species.Starling,
            Species.FeralFish
        ];

        if (!speciesForKnownRegions.includes(species)) {
            return;
        }

        const speciesKmlName = speciesName?.replace(/scan|alert/g, "");
        if (!speciesKmlName) {
            return;
        }

        const url = `${AppSettings.kmlUrl}${AppSettings.basePath}/GetKML?pathInfo=knownRegions%2F${speciesKmlName}_known_region.kml`;;

        const isEnabled = filter.layers.knownRegions;

        const matchesLayer = (layer: google.maps.KmlLayer) =>
            layer.getUrl?.()?.includes(`knownRegions%2F${speciesKmlName}_known_region.kml`);

        if (isEnabled) {
            createKmlLayer(url, false, true, "knownregion");
        } else {
            const remaining: google.maps.KmlLayer[] = [];

            kmlLayersRef.current.forEach(layer => {
                if (matchesLayer(layer)) {
                    layer.setMap(null);
                } else {
                    remaining.push(layer);
                }
            });

            kmlLayersRef.current = remaining;
        }
    }, [map, kmlApi, filter.layers.knownRegions, species, speciesName]);

    // -- Rabbit disease extras --
    useEffect(() => {
        if (!map || !kmlApi) {
            return;
        }

        if (species !== Species.Rabbit || !filter.recordType.disease) {
            return;
        }

        const matches = {
            releaseSites: (layer: google.maps.KmlLayer) => {
                return layer.getUrl?.()?.includes(`releaseSites%2Frabbit.disease.releasesites`)
            },

            boostSites: (layer: google.maps.KmlLayer) => {
                return layer.getUrl?.()?.includes(`releaseSites%2Frhd.boost.sites`)
            },

            eoiSites: (layer: google.maps.KmlLayer) => {
                return layer.getUrl?.()?.includes(`releaseSites%2Frabbit.eoi.release`)
            }
        };

        const removeOrKeep = (matchFn: (l: google.maps.KmlLayer) => boolean) => {
            const remaining: google.maps.KmlLayer[] = [];

            kmlLayersRef.current.forEach(layer => {
                if (matchFn(layer)) {
                    layer.setMap(null);
                } else {
                    remaining.push(layer);
                }
            });

            kmlLayersRef.current = remaining;
        };

        // Release Sites
        if (filter.layers.releaseSites) {
            const url = `${AppSettings.kmlUrl}${AppSettings.basePath}/GetKML?pathInfo=releaseSites%2Frabbit.disease.releasesites.zip`;

            createKmlLayer(url, false, true, "disease");
        } else {
            removeOrKeep(matches.releaseSites);
        }

        // RHD Boost Sites
        if (
            (isAdminOrSuperAdmin || isRabbitDiseaseK5StateCoordinators) &&
            filter.layers.rhdBoostRollOutSites
        ) {
            const url = `${AppSettings.kmlUrl}${AppSettings.basePath}/GetKML?pathInfo=releaseSites%2Frhd.boost.sites.zip`;

            createKmlLayer(url, true, true, "disease");
        } else {
            removeOrKeep(matches.boostSites);
        }

        // Successful EOI Sites
        if (
            (isAdminOrSuperAdmin || isRabbitDiseaseK5StateCoordinators) &&
            filter.layers.successfulEoiSites
        ) {
            const url = `${AppSettings.kmlUrl}${AppSettings.basePath}/GetKML?pathInfo=releaseSites%2Frabbit.eoi.release.zip`;

            createKmlLayer(url, true, true, "disease");
        } else {
            removeOrKeep(matches.eoiSites);
        }
    }, [
        map,
        kmlApi,
        species,
        filter.recordType.disease,
        filter.layers.releaseSites,
        filter.layers.rhdBoostRollOutSites,
        filter.layers.successfulEoiSites,
        isAdminOrSuperAdmin,
        isRabbitDiseaseK5StateCoordinators
    ]);

    return null;
};

export default KmlLayer;